"use client";
import React from "react";
import FormRow from "../FormRow";
import Label from "../Label";
import PasswordRequirement from "./PasswordRequirement";
import {
    PasswordStatus,
    ISetPasswordProfile,
    IValidationChecklistProps
} from "./validatePasswordTypes";

const upperReg = /[A-Z]/;
const lowerReg = /[a-z]/;
const spaceReg = /^[a-zA-Z0-9!@#$%^&_*]+$/;
const numberOrSpecialCharReg = /(?=.*[!@#$%^&*0-9])/;

const IsLengthValid = (password: string): boolean =>
    password.length >= 8 && password.length <= 40;

const IsContainingNumSym = (password: string): boolean => numberOrSpecialCharReg.test(password);

const IsOnlyValidChars = (password: string): boolean => spaceReg.test(password);

const IsUpperAndLower = (password: string): boolean =>
    upperReg.test(password) && lowerReg.test(password);

const ContainsProfileInfo = (password: string, profile: ISetPasswordProfile): boolean => {
    const emailLocalPart = (profile.email as string).split("@")[0].toLowerCase();
    const lowerPw = password.toLowerCase();
    return (
        (emailLocalPart.length > 2 && lowerPw.includes(emailLocalPart)) ||
        ((profile.userName as string).length > 2 &&
            lowerPw.includes((profile.userName as string).toLowerCase())) ||
        ((profile.firstName as string).length > 2 &&
            lowerPw.includes((profile.firstName as string).toLowerCase())) ||
        ((profile.lastName as string).length > 2 &&
            lowerPw.includes((profile.lastName as string).toLowerCase()))
    );
};

export const CalculatePasswordStatus = (
    profile: ISetPasswordProfile,
    newPassword: string,
    confirmNewPassword: string,
    currentPassword?: string
): PasswordStatus => {
    let status = PasswordStatus.VALID;

    if (newPassword.length > 0) {
        if (newPassword === currentPassword)
            status |= PasswordStatus.INVALID_MATCHES_CURRENT;

        if (ContainsProfileInfo(newPassword, profile))
            status |= PasswordStatus.INVALID_CONTAINS_PROFILE_INFO;

        if (!IsContainingNumSym(newPassword))
            status |= PasswordStatus.INVALID_MISSING_NUMBER_OR_SYMBOL;

        if (!IsOnlyValidChars(newPassword))
            status |= PasswordStatus.INVALID_DISALLOWED_CHARACTERS;

        if (!IsLengthValid(newPassword))
            status |= PasswordStatus.INVALID_LENGTH;

        if (!IsUpperAndLower(newPassword))
            status |= PasswordStatus.INVALID_CASE;
    }

    if (confirmNewPassword.length > 0 && newPassword !== confirmNewPassword)
        status |= PasswordStatus.INVALID_NOT_CONFIRMED;

    return status;
};

export const IsPasswordStrong = (status: PasswordStatus): boolean => (
    status & (
        PasswordStatus.INVALID_MISSING_NUMBER_OR_SYMBOL |
        PasswordStatus.INVALID_DISALLOWED_CHARACTERS |
        PasswordStatus.INVALID_LENGTH |
        PasswordStatus.INVALID_CASE
    )
) === PasswordStatus.VALID;

export const ErrorExists = (status: PasswordStatus, error: PasswordStatus): boolean =>
    (status & error) !== PasswordStatus.VALID;

export const ValidationChecklist = (props: IValidationChecklistProps) => {
    return (
        <FormRow>
            <Label htmlFor={"new-password"}>
                {props.passwordResources.PasswordRequirements}
            </Label>
            <PasswordRequirement
                id="saif-password-length"
                requirementMet={
                    !ErrorExists(props.newPasswordStatus, PasswordStatus.INVALID_LENGTH)
                }
                invalidMessage={props.passwordResources.ValLengthRequirement}
                Disabled={!props.isNewDirty}
            />
            <PasswordRequirement
                id="saif-password-spaces"
                requirementMet={
                    !ErrorExists(
                        props.newPasswordStatus,
                        PasswordStatus.INVALID_DISALLOWED_CHARACTERS
                    )
                }
                invalidMessage={props.passwordResources.ValCharacterRequirement}
                Disabled={!props.isNewDirty}
            />
            <PasswordRequirement
                id="saif-password-upper-and-lower"
                requirementMet={
                    !ErrorExists(props.newPasswordStatus, PasswordStatus.INVALID_CASE)
                }
                invalidMessage={props.passwordResources.ValCaseRequirement}
                Disabled={!props.isNewDirty}
            />
            <PasswordRequirement
                id="saif-password-number-symbol"
                requirementMet={
                    !ErrorExists(
                        props.newPasswordStatus,
                        PasswordStatus.INVALID_MISSING_NUMBER_OR_SYMBOL
                    )
                }
                invalidMessage={props.passwordResources.ValSymbolRequirement}
                Disabled={!props.isNewDirty}
            />
            <PasswordRequirement
                id="saif-password-name"
                requirementMet={
                    !ErrorExists(
                        props.newPasswordStatus,
                        PasswordStatus.INVALID_CONTAINS_PROFILE_INFO
                    )
                }
                invalidMessage={props.passwordResources.ValNameRequirement}
                Disabled={!props.isNewDirty}
            />
            <PasswordRequirement
                id="saif-password-match"
                requirementMet={
                    !ErrorExists(
                        props.newPasswordStatus,
                        PasswordStatus.INVALID_NOT_CONFIRMED
                    )
                }
                invalidMessage={props.passwordResources.ValConfMatchingRequirement}
                Disabled={!props.isNewDirty || !props.isConfirmDirty}
            />
            {props.isCurrentDirty != null && (
                <PasswordRequirement
                    id="saif-password-reuse"
                    requirementMet={
                        !ErrorExists(
                            props.newPasswordStatus,
                            PasswordStatus.INVALID_MATCHES_CURRENT
                        )
                    }
                    invalidMessage={props.passwordResources.ValNewCurrentMatchingRequirement as string}
                    Disabled={!props.isCurrentDirty || !props.isNewDirty}
                />
            )}
        </FormRow>
    )
}